import { atom as g, map as m, computed as y } from "nanostores";
import { copilotTools as x, copilotInstructions as C, copilotContext as F, uniqueId as S } from "./global-state.js";
function w() {
  return Math.floor(Date.now() / 1e3);
}
const v = {
  idle: ["initializing", "ending"],
  initializing: ["ready", "error"],
  ready: ["sending", "ending"],
  sending: ["receiving"],
  receiving: ["ready"],
  ending: ["ended", "error"],
  error: ["initializing", "ending"],
  ended: []
  // Can only transition to itself (terminal state)
};
function M(i, e) {
  if (i === e)
    return i;
  if (!v[i])
    throw new Error(`Unknown status: ${i}`);
  if (v[i].includes(e))
    return e;
  throw new Error(`Invalid status transition: ${i} -> ${e}`);
}
class P {
  constructor(e) {
    this.config = {
      includeGlobalContext: !0,
      includeGlobalTools: !0,
      includeGlobalInstructions: !0
    }, this.__status = g("idle"), this.__messages = g([]), this.__meta = g({}), this.__uploadFiles = g([]), this.__serverSideTools = m({}), this.__sessionInstructions = m({}), this.__sessionTools = m({}), this.__sessionContext = m({}), this.__tools = y(
      [this.__serverSideTools, x, this.__sessionTools],
      (t, o, n) => {
        const a = {}, s = {
          ...this.config.includeGlobalTools === !0 ? o : {},
          ...n
        };
        for (const r of Object.values(t))
          if (r != null) {
            const h = s[r.name];
            a[r.name] = {
              ...h?.type === "server" ? h : void 0,
              ...r
            };
          }
        for (const r of Object.values(s))
          r != null && r.type === "client" && a[r.name] == null && (a[r.name] = r);
        return a;
      }
    ), this.__statefulMessages = y([this.__messages, this.__tools], E), this.__instructions = y(
      [C, this.__sessionInstructions],
      (t, o) => ({
        ...this.config.includeGlobalInstructions === !0 ? t : {},
        ...o
      })
    ), this.__context = y([F, this.__sessionContext], (t, o) => ({
      ...this.config.includeGlobalContext === !0 ? t : {},
      ...o
    })), this.__suggestionsRefreshTrigger = g([]), e && (this.config = { ...this.config, ...e });
  }
  /**
   * The current status of the Copilot session.
   */
  get status() {
    return this.__status.get();
  }
  /**
   * If the session is being ended.
   */
  get isEnding() {
    return this.status === "ending";
  }
  get isEnded() {
    return this.status === "ended";
  }
  /**
   * Sets the status of the Copilot session.
   */
  setStatus(e) {
    this.__status.set(M(this.status, e));
  }
  /**
   * adds an instruction just for this session
   */
  addInstruction(e) {
    const t = S();
    return this.__sessionInstructions.setKey(t, e), () => {
      this.__sessionInstructions.setKey(t, void 0);
    };
  }
  /**
   * Adds a tool just for this session
   */
  addTool(e) {
    return this.__sessionTools.setKey(e.name, e), () => {
      this.__sessionTools.setKey(e.name, void 0);
    };
  }
  /**
   * Adds a context just for this session
   */
  addContext(e) {
    const t = S();
    return this.__sessionContext.setKey(t, e), () => {
      this.__sessionContext.setKey(t, void 0);
    };
  }
  /**
   * Sets the meta data for this session
   */
  setSessionMeta(e) {
    this.__meta.set(e);
  }
  /**
   * Gets the meta data for this session
   */
  getSessionMeta() {
    return this.__meta.get();
  }
  /**
   * Updates the meta data for a specific message by targeting message.meta.id
   */
  setMessageMeta(e, t) {
    const n = this.__messages.get().map((a) => a.role === "model" && a.type === "meta" && "meta" in a && a.meta?.id === e ? {
      ...a,
      meta: t
    } : a);
    this.__messages.set(n);
  }
  /**
   * Forces a refresh of suggestions for this session.
   * This will trigger any active suggestion hooks to regenerate their suggestions.
   * @param instruction - Custom instructions to include in the next suggestions fetch.
   * These instructions will be used once and then cleared.
   */
  refreshSuggestions(e) {
    this.__suggestionsRefreshTrigger.set([{ instruction: e }]);
  }
  /**
   * Initializes a new session, either from an existing session id,
   * or by creating a new one
   */
  async initialize(e) {
    if (this.status !== "idle" && this.status !== "error")
      return this.id;
    this.setStatus("initializing");
    try {
      const t = window.__hydra_copilot__?.startSession;
      if (t == null)
        throw new Error("Copilot session not configured. Please set the Copilot configuration first.");
      if (e == null) {
        const o = await t();
        if (o.id == null)
          throw new Error("Failed to start session, no session ID returned");
        this.id = o.id, this.__serverSideTools.set(b(o.tools ?? []));
      } else {
        this.id = e;
        const o = await this.loadSession();
        this.__messages.set(o.messages ?? []), this.__meta.set(o.meta ?? {}), this.__serverSideTools.set(b(o.tools ?? []));
      }
      return this.setStatus("ready"), this.id;
    } catch (t) {
      throw this.setStatus("error"), t;
    }
  }
  /**
   * Ends the current session, clearing all messages and state.
   */
  async end() {
    this.setStatus("ending");
    try {
      const e = window.__hydra_copilot__?.endSession;
      if (e == null)
        throw new Error("Copilot session not configured. Please set the Copilot configuration first.");
      this.id != null && await e(this.id), this.__messages.set([]), this.id = void 0, this.__sessionInstructions.set({}), this.__sessionTools.set({}), this.__sessionContext.set({}), this.__serverSideTools.set({}), this.__meta.set({}), this.setStatus("ended");
    } catch (e) {
      throw this.setStatus("error"), e;
    }
  }
  async sendMessage(e) {
    const t = typeof e == "string" ? [{ role: "user", type: "text", text: e, timestamp: w() }] : e, o = window.__hydra_copilot__?.sendStreamingMessage;
    if (o == null)
      throw new Error("Copilot session not configured. Please set the Copilot configuration first.");
    if (this.status === "idle" && await this.initialize(), this.status !== "ready")
      throw new Error(`Cannot send message while session is in "${this.status}" state.`);
    try {
      this.setStatus("sending");
      let n = t.map((l) => ({
        ...l,
        timestamp: l.timestamp || w()
      })), a = 0;
      for (; n.length > 0; ) {
        this.__messages.set([...this.__messages.get(), ...n]);
        const l = [], s = (d) => {
          if (this.setStatus("receiving"), a += 1, this.__messages.set([...this.__messages.get(), d]), d.role === "model" && d.type == "tool_call")
            for (const u of d.tool_calls) {
              if (u.is_client_tool !== !0)
                continue;
              const c = this.__tools.get()[u.name];
              l.push(
                (async () => {
                  try {
                    if (c?.type === "server")
                      throw new Error(
                        `Tool ${u.name} is a server-side tool and cannot be called on the client.`
                      );
                    if (c?.handler == null)
                      throw new Error(`Tool ${u.name} does not have a handler defined.`);
                    const _ = await c.handler({ args: u.args, session: this, toolCallId: u.id }) ?? null;
                    return {
                      tool_call_id: u.id,
                      name: u.name,
                      response: { result: _, error: null },
                      finished: !0
                    };
                  } catch (_) {
                    return {
                      tool_call_id: u.id,
                      name: u.name,
                      response: {
                        result: null,
                        error: `Error calling tool ${u.name}: ${_ instanceof Error ? _.message : String(_)}`
                      },
                      finished: !0
                    };
                  }
                })()
              );
            }
          if (d.role === "user" && d.type === "tool_response")
            for (const u of d.tool_responses) {
              const c = this.__tools.get()[u.name], _ = R(
                [...this.__messages.get(), ...n],
                u.tool_call_id
              );
              c?.type === "server" && c?.handler != null && u.finished === !0 && c.handler({
                args: _?.args || {},
                session: this,
                response: u.response,
                toolCallId: u.tool_call_id
              });
            }
        }, r = {
          id: this.id,
          messages: n,
          tools: I(this.__tools.get()),
          instructions: U(this.__instructions.get()),
          context: j(this.__context.get()),
          onMessage: s
        };
        if (await o(r), l.length === 0) {
          n = [];
          continue;
        }
        n = [
          {
            role: "user",
            type: "tool_response",
            tool_responses: await Promise.all(l),
            timestamp: w()
          }
        ];
      }
      if (a === 0)
        throw new Error("No messages received from the server. Please check your connection.");
      this.setStatus("ready");
    } catch (n) {
      throw this.setStatus("receiving"), this.setStatus("ready"), n;
    }
  }
  /**
   * Loads the session data (messages and meta)
   */
  async loadSession() {
    const e = window.__hydra_copilot__?.getSession;
    if (e == null)
      throw new Error("Copilot session not configured. Please set the Copilot configuration first.");
    if (this.id == null)
      throw new Error("Session not started");
    return await e(this.id);
  }
  /**
   * Waits for the session to be ready
   */
  async waitUntilReady() {
    if (this.status !== "ready") {
      if (this.status === "idle") {
        await this.initialize();
        return;
      }
      if (this.status === "initializing")
        return new Promise((e, t) => {
          let o = null, n = null;
          const a = () => {
            o !== null && (clearTimeout(o), o = null), n !== null && (n(), n = null);
          };
          n = this.__status.subscribe((l) => {
            l === "ready" ? (a(), e()) : l === "error" ? (a(), t(new Error("Session initialization failed"))) : l !== "initializing" && (a(), t(new Error(`Unexpected session status: ${l}`)));
          }), o = setTimeout(() => {
            a(), t(new Error("Session initialization timeout - exceeded 30 seconds"));
          }, 3e4);
        });
      throw this.status === "error" ? new Error("Session is in error state") : new Error(`Cannot wait for session in "${this.status}" state`);
    }
  }
  /**
   * Gets upload signature for file upload
   * Note: Session readiness is now checked at the upload level (uploadCopilotFile)
   */
  async getUploadSignature(e) {
    if (!this.id)
      throw new Error("Session ID not available - ensure session is ready before calling getUploadSignature");
    const t = window.__hydra_copilot__;
    if (!t?.getUploadSignature)
      throw new Error("File upload not configured. Please provide getUploadSignature in setCopilotConfig.");
    return await t.getUploadSignature({
      id: this.id,
      size: e.size,
      mime_type: e.mime_type
    });
  }
  /**
   * Gets download URL for a previously uploaded file
   */
  async getFileDownloadUrl(e) {
    if (!this.id)
      throw new Error("Session ID not available - ensure session is ready before calling getFileDownloadUrl");
    const t = window.__hydra_copilot__;
    if (!t?.getFileDownloadUrl)
      throw new Error("File download not configured. Please provide getFileDownloadUrl in setCopilotConfig.");
    return (await t.getFileDownloadUrl({
      id: this.id,
      filename: e
    })).url;
  }
  /**
   * Add files to the upload queue
   */
  addUploadFiles(e) {
    const t = this.__uploadFiles.get();
    this.__uploadFiles.set([...t, ...e]);
  }
  /**
   * Update a specific upload file
   */
  updateUploadFile(e, t) {
    const n = this.__uploadFiles.get().map((a) => a.id === e ? { ...a, ...t } : a);
    this.__uploadFiles.set(n);
  }
  /**
   * Remove upload file
   */
  removeUploadFile(e) {
    const o = this.__uploadFiles.get().filter((n) => n.id !== e);
    this.__uploadFiles.set(o);
  }
  /**
   * Clear all upload files
   */
  clearUploadFiles() {
    this.__uploadFiles.set([]);
  }
}
function T(i) {
  return typeof i == "string" ? i : typeof i == "number" || typeof i == "boolean" ? String(i) : Array.isArray(i) || typeof i == "object" ? JSON.stringify(i) : String(i);
}
function E(i, e) {
  const t = [];
  let o = [];
  const n = {}, a = i.filter(
    (s) => {
      if (s.role === "user" && s.type === "tool_response") {
        for (const r of s.tool_responses)
          r.tool_call_id != null && (n[r.tool_call_id] == null && (n[r.tool_call_id] = []), n[r.tool_call_id].push(r));
        return !1;
      }
      return s.type === "text" || s.type === "content" || s.type === "tool_call" || s.type == "meta" || s.type === "error";
    }
  );
  function l() {
    const s = o.filter((c) => c.type === "text"), r = o.find((c) => c.type === "meta" && "meta" in c);
    if (o = [], s.length === 0)
      return;
    const h = s.map((c) => c.text).join(""), p = r?.meta ?? {}, d = s[0]?.thought;
    let u;
    if (d === !0) {
      const c = t[t.length - 1];
      c != null ? u = Math.max(0, s[s.length - 1].timestamp - c.timestamp) : u = 0;
    }
    t.push({
      role: "model",
      type: "text",
      text: h,
      timestamp: s[0].timestamp,
      meta: p,
      ...d === !0 && { thought: d },
      ...u != null && { duration: u }
    }), o = [];
  }
  for (const s of a)
    if (s.role === "model" && s.type === "text") {
      const r = o.some((d) => d.type === "text"), h = s.thought === !0, p = o.some(
        (d) => d.type === "text" && "thought" in d && d.thought === !0
      );
      r && (h && !p || !h && p) && l(), o.push(s);
    } else
      s.role === "user" && (s.type === "text" || s.type === "content") ? (l(), t.push(s)) : s.role === "model" && s.type === "meta" ? (o.push(s), l()) : s.role === "model" && s.type === "tool_call" ? (l(), t.push({
        role: "model",
        type: "tool_call",
        tool_calls: s.tool_calls.map((r) => {
          const h = n[r.id], p = e[r.name];
          if (h == null || h.length === 0)
            return {
              ...r,
              status: "executing"
            };
          const d = h.find((_) => _.response.error != null && _.response.error !== ""), u = h.some((_) => _.finished === !0);
          if (d != null)
            return {
              ...r,
              status: "error",
              response: d.response
            };
          const c = p?.type === "server" && p?.isStreaming === !0;
          return u && h.length === 1 && !c ? {
            ...r,
            status: "success",
            response: h[0].response
          } : {
            ...r,
            status: u ? "success" : "executing",
            response: {
              result: h.map((_) => _.response.result),
              error: null
            }
          };
        }),
        timestamp: s.timestamp
      })) : s.role === "model" && s.type === "error" ? (l(), t.push(s)) : console.error("Unknown message type", s);
  return l(), t;
}
function I(i) {
  return Object.values(i).filter((e) => e !== void 0 && e.type === "client").map((e) => ({
    name: e.name,
    description: typeof e.description == "string" ? f(e.description) : e.description,
    request_json_schema: e.requestSchema,
    json_response_schema: e.responseSchema
  }));
}
function U(i) {
  return Array.isArray(i) ? i.map((e) => f(e.instruction).trim()) : Object.values(i).filter((e) => e !== void 0).map((e) => f(e.instruction).trim());
}
function j(i) {
  return Array.isArray(i) ? i.map((e) => ({
    description: f(e.description),
    value: T(typeof e.value == "function" ? e.value() : e.value)
  })) : Object.values(i).filter((e) => e !== void 0).map((e) => ({
    description: f(e.description),
    value: T(typeof e.value == "function" ? e.value() : e.value)
  }));
}
function f(i) {
  const e = i.split(`
`), t = e.filter((l) => l.trim().length > 0);
  if (t.length === 0)
    return i;
  const o = t.map((l) => {
    const s = l.match(/^(\s*)/);
    return s ? s[1].length : 0;
  }), n = Math.min(...o);
  return e.map((l) => l.trim().length === 0 ? l : l.slice(n)).join(`
`);
}
function R(i, e) {
  for (const t of i)
    if (t.role === "model" && t.type === "tool_call") {
      const o = t.tool_calls.find((n) => n.id === e);
      if (o)
        return o;
    }
}
function b(i) {
  const e = {};
  for (const t of i)
    e[t.name] = {
      type: "server",
      name: t.name,
      description: t.description,
      requestSchema: t.request_json_schema,
      responseSchema: t.response_json_schema
    };
  return e;
}
export {
  P as CopilotSession,
  R as findToolCallById,
  j as prepareContextForTransport,
  U as prepareInstructionsForTransport,
  I as prepareToolsForTransport,
  b as serverSideToolsToCopilotTools,
  T as stringifyForCopilot,
  f as undent,
  M as updatedStatus
};
